import {
  Button,
  SequenceMenu,
  IconPlus,
  MagicTableCell,
  MagicTableCellEmpty,
  MagicTable,
  useMagicTableInfiniteQuery,
  MagicTableEmptyState,
  MagicTableFilterEmptyState,
  IconUserDuo
} from '@sequencehq/core-components'
import { dateTimeWithFormat } from '@sequencehq/utils'
import { CurrentUserId } from 'components/CurrentUserId/CurrentUserId'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import { useNavigate } from 'react-router-dom'
import { SeatTypeBadge } from 'Seats/common/SeatTypeBadge'
import { CreateSeatTypeDrawer } from 'Seats/SeatMetric/CreateSeatTypeDrawer'
import { ProtectedButton, usePermissions } from 'lib/rbac'

type SeatMetric = DashboardApi20240730.GetSeatMetrics.SeatMetric

export const SeatTypesMagicTable = () => {
  const navigate = useNavigate()
  const { can } = usePermissions()

  const { infiniteQuery } = useMagicTableInfiniteQuery(
    dashboard20240730Client.getSeatMetrics
  )

  return (
    <CurrentUserId>
      {userId => (
        <MagicTable<{ model: SeatMetric }>
          entityNamePlural="seat metrics"
          sequenceUserId={userId}
          infiniteQuery={infiniteQuery}
          columns={[
            {
              id: 'label',
              accessorKey: 'label',
              header: 'Name',
              cell: value => (
                <MagicTableCell text={value.getValue<SeatMetric['label']>()} />
              )
            },
            {
              id: 'seatType',
              accessorKey: 'seatType',
              header: 'Seat type',
              cell: value => (
                <MagicTableCellEmpty>
                  <SeatTypeBadge
                    seatType={value.getValue<SeatMetric['seatType']>()}
                  />
                </MagicTableCellEmpty>
              )
            },
            {
              id: 'createdAt',
              accessorKey: 'createdAt',
              header: 'Created At (UTC)',
              cell: value => {
                const createdAt = value.getValue<SeatMetric['createdAt']>()

                return (
                  <MagicTableCell
                    text={dateTimeWithFormat(
                      createdAt,
                      'dd-MM-yyyy ∙ HH:mm:ss'
                    )}
                  />
                )
              }
            }
          ]}
          kebabMenu={(model, props) => {
            if (!can('org.usage:update')) {
              return null
            }

            return (
              <SequenceMenu
                data-testid={`seatMetrics.${model.id}.menu`}
                buttonSize="small"
                alignment="bottom-right"
                closeMenuOnItemClick
                items={[
                  <SequenceMenu.Item
                    key="edit-seat-metric"
                    data-testid={`seatMetrics.${model.id}.edit`}
                    uuid="edit-seat-metric"
                    label="Edit seat metric"
                    onClick={() => navigate(`/seats/${model.id}/edit`)}
                  />
                ]}
                {...props}
              />
            )
          }}
          emptyStateUnfiltered={entityNamePlural => (
            <MagicTableEmptyState
              data-testid="seatMetrics.emptyState"
              entityNamePlural={entityNamePlural}
              imageSrc="/img/empty/gauge.png"
              learnMoreText="Seat metrics represent the types of seats that exist in your product"
              learnMoreHref="https://docs.sequencehq.com/seats/overview"
              buttonNew={
                can('org.usage:create') ? (
                  <CreateSeatTypeDrawer
                    trigger={
                      <Button
                        variant="primary"
                        leadingIcon={<IconPlus />}
                        data-testid="seatMetrics.add"
                      >
                        New seat metric
                      </Button>
                    }
                  />
                ) : (
                  <ProtectedButton permission="org.usage:create">
                    <Button
                      variant="primary"
                      leadingIcon={<IconPlus />}
                      data-testid="seatMetrics.add"
                    >
                      New seat metric
                    </Button>
                  </ProtectedButton>
                )
              }
            />
          )}
          emptyStateFiltered={(entityNamePlural, clearFilters) => (
            <MagicTableFilterEmptyState
              entityNamePlural={entityNamePlural}
              entityIcon={IconUserDuo}
              actionClearFilters={clearFilters}
              actionViewAll={() => navigate('/seats')}
              variant="TABLE"
            />
          )}
        />
      )}
    </CurrentUserId>
  )
}
